/**
 * Seamless Infinite Logo Slider
 *
 * The scrolling motion itself is a plain CSS animation (see the
 * @keyframes in the section's style block) with a working default
 * duration already set server-side in Liquid — the slider moves
 * correctly even if this script never runs. This script's only job
 * is to measure the real rendered width of one logo set and refine
 * the animation-duration to match, so the scroll speed stays
 * visually constant (px/second) no matter how many logos are added.
 *
 * Supports multiple independent instances on the same page and
 * re-initializes correctly inside the Shopify theme editor.
 */
(function () {
  'use strict';

  var instances = {};

  function BCLogoSlider(root) {
    this.root = root;
    this.track = root.querySelector('.bc-logo-slider__track');
    this.firstSet = root.querySelector('.bc-logo-slider__set');
    this.rate = parseFloat(root.getAttribute('data-rate')) || 60; // px/second

    this.onResize = this.debounce(this.applyDuration.bind(this), 200);

    if (this.track && this.firstSet) {
      this.init();
    }
  }

  BCLogoSlider.prototype.init = function () {
    this.applyDuration();

    var images = this.track.querySelectorAll('img');
    var pending = 0;
    images.forEach(
      function (img) {
        if (!img.complete) {
          pending += 1;
          img.addEventListener('load', onDone.bind(this), { once: true });
          img.addEventListener('error', onDone.bind(this), { once: true });
        }
      }.bind(this)
    );

    function onDone() {
      pending -= 1;
      if (pending === 0) this.applyDuration();
    }

    window.addEventListener('resize', this.onResize);
  };

  BCLogoSlider.prototype.applyDuration = function (retriesLeft) {
    var width = this.firstSet.getBoundingClientRect().width;

    if (!width) {
      // Layout hasn't settled yet (rare, but possible right at
      // paint time). Try again on the next couple of frames rather
      // than leaving the CSS fallback duration in place forever.
      if (retriesLeft === undefined) retriesLeft = 5;
      if (retriesLeft > 0) {
        window.requestAnimationFrame(
          function () {
            this.applyDuration(retriesLeft - 1);
          }.bind(this)
        );
      }
      return;
    }

    if (!this.rate) return;
    var durationSeconds = width / this.rate;
    this.track.style.animationDuration = durationSeconds + 's';
  };

  BCLogoSlider.prototype.debounce = function (fn, wait) {
    var timer;
    return function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(fn, wait);
    };
  };

  BCLogoSlider.prototype.destroy = function () {
    window.removeEventListener('resize', this.onResize);
  };

  function initSection(root) {
    var id = root.getAttribute('data-section-id');
    if (!id || instances[id]) return;
    instances[id] = new BCLogoSlider(root);
  }

  function destroySection(id) {
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  }

  function initAll(context) {
    var scope = context || document;
    var sections = scope.querySelectorAll('.bc-logo-slider');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor support: re-init on section load, tear down on
  // unload, so edits and re-orders in the customizer keep working.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('.bc-logo-slider') : null;
    if (root) initSection(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('.bc-logo-slider') : null;
    if (root) destroySection(root.getAttribute('data-section-id'));
  });
})();